import { Trimester, trimesterForWeek } from "./nutritionEngine";

const DAY_MS = 24 * 60 * 60 * 1000;
const GESTATION_DAYS = 280; // 40 weeks from LMP, standard obstetric convention
const CONCEPTION_OFFSET_DAYS = 14; // average ovulation/conception ~2 weeks after LMP

export type PregnancyDates = {
  lmpDate: string; // ISO yyyy-mm-dd
  dueDate: string;
  conceptionDate: string;
};

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function parseIso(s: string): Date {
  return new Date(`${s}T00:00:00Z`);
}

/**
 * Given exactly one of lmpDate, dueDate, or conceptionDate, derive the other
 * two using standard 280-day gestation / 14-day conception-offset
 * conventions, per onboarding step 4.
 */
export function derivePregnancyDates(
  input:
    | { lmpDate: string; dueDate?: undefined; conceptionDate?: undefined }
    | { dueDate: string; lmpDate?: undefined; conceptionDate?: undefined }
    | { conceptionDate: string; lmpDate?: undefined; dueDate?: undefined }
): PregnancyDates {
  if (input.lmpDate) {
    const lmp = parseIso(input.lmpDate);
    return {
      lmpDate: input.lmpDate,
      dueDate: toIso(new Date(lmp.getTime() + GESTATION_DAYS * DAY_MS)),
      conceptionDate: toIso(new Date(lmp.getTime() + CONCEPTION_OFFSET_DAYS * DAY_MS)),
    };
  }
  if (input.dueDate) {
    const due = parseIso(input.dueDate);
    const lmp = new Date(due.getTime() - GESTATION_DAYS * DAY_MS);
    return {
      lmpDate: toIso(lmp),
      dueDate: input.dueDate,
      conceptionDate: toIso(new Date(lmp.getTime() + CONCEPTION_OFFSET_DAYS * DAY_MS)),
    };
  }
  if (!input.conceptionDate) {
    throw new Error("One of lmpDate, dueDate, or conceptionDate is required");
  }
  const conception = parseIso(input.conceptionDate);
  const lmp = new Date(conception.getTime() - CONCEPTION_OFFSET_DAYS * DAY_MS);
  return {
    lmpDate: toIso(lmp),
    dueDate: toIso(new Date(lmp.getTime() + GESTATION_DAYS * DAY_MS)),
    conceptionDate: input.conceptionDate,
  };
}

export function gestationalWeekFor(lmpDate: string, today: Date = new Date()): number {
  const lmp = parseIso(lmpDate);
  const daysSince = Math.floor((today.getTime() - lmp.getTime()) / DAY_MS);
  return Math.max(0, Math.floor(daysSince / 7));
}

export function gestationalAgeFor(
  lmpDate: string,
  today: Date = new Date()
): { week: number; day: number; trimester: Trimester } {
  const lmp = parseIso(lmpDate);
  const daysSince = Math.max(0, Math.floor((today.getTime() - lmp.getTime()) / DAY_MS));
  const week = Math.floor(daysSince / 7);
  const day = daysSince % 7;
  return { week, day, trimester: trimesterForWeek(week) };
}

export function calculateAge(dateOfBirth: string, today: Date = new Date()): number {
  const dob = parseIso(dateOfBirth);
  let age = today.getUTCFullYear() - dob.getUTCFullYear();
  const monthDiff = today.getUTCMonth() - dob.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < dob.getUTCDate())) {
    age--;
  }
  return age;
}
